import React, { useEffect, useState, Fragment } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { NavBar } from '../components/NavBar';
import { Card } from '../components/SubscriptionAppCard';
import { Badge } from '../components/SubscriptionAppBadge';
import { Button } from '../components/SubscriptionAppButton';
import { RefreshCw, Package, ArrowRight, X, ChevronDown, ChevronUp, Info, AlertTriangle, CheckCircle, Search, Wine, ArrowRightLeft, Lightbulb } from 'lucide-react';
// Mock product data
const mockProducts = [{
  id: '1',
  name: 'Acme Shiraz 2022',
  sku: 'SHIRAZ-2022-750',
  price: 45.0,
  shopifyPrice: 45.0,
  stock: 12,
  image: '🍷',
  subscriptionCount: 23
}, {
  id: '2',
  name: 'Acme Shiraz 2023',
  sku: 'SHIRAZ-2023-750',
  price: 48.0,
  shopifyPrice: 48.0,
  stock: 156,
  image: '🍷',
  subscriptionCount: 12,
  hasPriceMismatch: true,
  subscriptionPrice: 45.0
}, {
  id: '3',
  name: 'Acme Chardonnay 2021',
  sku: 'CHARD-2021-750',
  price: 38.0,
  shopifyPrice: 40.0,
  stock: 45,
  image: '🍷',
  subscriptionCount: 15,
  hasPriceMismatch: true,
  subscriptionPrice: 38.0
}, {
  id: '4',
  name: 'Acme Chardonnay 2022',
  sku: 'CHARD-2022-750',
  price: 40.0,
  shopifyPrice: 42.0,
  stock: 89,
  image: '🍷',
  subscriptionCount: 8,
  hasPriceMismatch: true,
  subscriptionPrice: 40.0
}, {
  id: '5',
  name: '2023 Reserve Cabernet',
  sku: '23RCAB-750',
  price: 42.0,
  shopifyPrice: 42.0,
  stock: 45,
  image: '🍷',
  subscriptionCount: 89
}, {
  id: '6',
  name: '2024 Sparkling Brut',
  sku: '24SPB-750',
  price: 35.0,
  shopifyPrice: 35.0,
  stock: 0,
  image: '🥂',
  subscriptionCount: 34
}, {
  id: '7',
  name: '2023 Rosé',
  sku: '23RSE-750',
  price: 32.0,
  shopifyPrice: 32.0,
  stock: 15,
  image: '🍷',
  subscriptionCount: 42
}, {
  id: '8',
  name: '2025 Chardonnay',
  sku: '25CHD3-750',
  price: 38.0,
  shopifyPrice: 38.0,
  stock: 130,
  image: '🍷',
  subscriptionCount: 120
}, {
  id: '9',
  name: '2024 Sauvignon Blanc',
  sku: '24SB2-750',
  price: 28.0,
  shopifyPrice: 28.0,
  stock: 100,
  image: '🍷',
  subscriptionCount: 95
}, {
  id: '10',
  name: '2024 Shiraz',
  sku: '24SHZ9-750',
  price: 45.0,
  shopifyPrice: 45.0,
  stock: 200,
  image: '🍷',
  subscriptionCount: 156
}, {
  id: '11',
  name: '2024 Pinot Noir',
  sku: '24PN5-750',
  price: 48.0,
  shopifyPrice: 48.0,
  stock: 180,
  image: '🍷',
  subscriptionCount: 67
}, {
  id: '12',
  name: '2023 Merlot',
  sku: '23MRL-750',
  price: 36.0,
  shopifyPrice: 36.0,
  stock: 250,
  image: '🍷',
  subscriptionCount: 78
}];
const subscriptionSummaryData: Record<string, {
  total: number;
  active: number;
  paused: number;
  failedPayment: number;
  nextBilling: string;
  totalUnits: number;
  plans: {
    name: string;
    count: number;
  }[];
}> = {
  '23RCAB-750': {
    total: 89,
    active: 82,
    paused: 5,
    failedPayment: 2,
    nextBilling: 'Feb 15, 2026',
    totalUnits: 134,
    plans: [{
      name: 'Mixed Dozen',
      count: 45
    }, {
      name: 'Premium Reds',
      count: 28
    }, {
      name: 'White Wine Lovers',
      count: 16
    }]
  },
  '24SPB-750': {
    total: 34,
    active: 31,
    paused: 3,
    failedPayment: 0,
    nextBilling: 'Feb 18, 2026',
    totalUnits: 34,
    plans: [{
      name: 'Celebration Club',
      count: 18
    }, {
      name: 'Mixed Dozen',
      count: 12
    }, {
      name: 'Sparkling Selection',
      count: 4
    }]
  }
};
// Mock affected subscriptions
const mockAffectedSubscriptions = [{
  id: 'SUB-23144',
  customer: 'Sarah Johnson',
  quantity: 2,
  currentPrice: 90.0,
  nextOrder: '2026-01-15',
  selected: true
}, {
  id: 'SUB-23141',
  customer: 'Michael Chen',
  quantity: 1,
  currentPrice: 45.0,
  nextOrder: '2026-01-18',
  selected: true
}, {
  id: 'SUB-23139',
  customer: 'Emma Williams',
  quantity: 3,
  currentPrice: 135.0,
  nextOrder: '2026-01-20',
  selected: true
}, {
  id: 'SUB-23138',
  customer: 'James Anderson',
  quantity: 2,
  currentPrice: 90.0,
  nextOrder: '2026-01-22',
  selected: true
}, {
  id: 'SUB-23137',
  customer: 'Olivia Martinez',
  quantity: 1,
  currentPrice: 45.0,
  nextOrder: '2026-01-25',
  selected: true
}];
export function ProductReplacePage() {
  const navigate = useNavigate();
  const location = useLocation();
  // Operation type state
  const [operationType, setOperationType] = useState<'replace' | 'sync'>('replace');
  // State
  const [howItWorksExpanded, setHowItWorksExpanded] = useState(true);
  const [originalProduct, setOriginalProduct] = useState<(typeof mockProducts)[0] | null>(null);
  const [replacementProduct, setReplacementProduct] = useState<(typeof mockProducts)[0] | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<(typeof mockProducts)[0] | null>(null);
  const [originalSearchQuery, setOriginalSearchQuery] = useState('');
  const [replacementSearchQuery, setReplacementSearchQuery] = useState('');
  const [syncSearchQuery, setSyncSearchQuery] = useState('');
  const [showOriginalDropdown, setShowOriginalDropdown] = useState(false);
  const [showReplacementDropdown, setShowReplacementDropdown] = useState(false);
  const [showSyncDropdown, setShowSyncDropdown] = useState(false);
  const [showSameProductPrompt, setShowSameProductPrompt] = useState(false);
  // Replace options state
  const [priceHandling, setPriceHandling] = useState('keep-original');
  const [customPercentage, setCustomPercentage] = useState(100);
  const [notifyCustomers, setNotifyCustomers] = useState(true);
  const [includeReason, setIncludeReason] = useState(true);
  const [replacementReason, setReplacementReason] = useState('');
  const [timing, setTiming] = useState('immediate');
  const [scheduledDate, setScheduledDate] = useState('');
  // Affected subscriptions state
  const [affectedSubscriptions, setAffectedSubscriptions] = useState(mockAffectedSubscriptions);
  const [showAllSubscriptions, setShowAllSubscriptions] = useState(false);
  // Modal state
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showSuccessBanner, setShowSuccessBanner] = useState(false);
  // Recent replacements state
  const [recentReplacementsExpanded, setRecentReplacementsExpanded] = useState(false);
  // Calculate price difference
  const priceDifference = originalProduct && replacementProduct ? replacementProduct.price - originalProduct.price : 0;
  const hasPriceDifference = Math.abs(priceDifference) > 0.01;
  // Handle pre-selection from navigation state
  useEffect(() => {
    if (location.state && (location.state as any).preSelectedProduct) {
      const sku = (location.state as any).preSelectedProduct;
      const product = mockProducts.find(p => p.sku === sku);
      if (product) {
        setOriginalProduct(product);
      }
    }
  }, [location.state]);
  // Check if same product selected
  useEffect(() => {
    if (operationType === 'replace' && originalProduct && replacementProduct && originalProduct.id === replacementProduct.id) {
      setShowSameProductPrompt(true);
    } else {
      setShowSameProductPrompt(false);
    }
  }, [operationType, originalProduct, replacementProduct]);
  // Filter products for search
  const filteredOriginalProducts = mockProducts.filter(p => p.name.toLowerCase().includes(originalSearchQuery.toLowerCase()) || p.sku.toLowerCase().includes(originalSearchQuery.toLowerCase()));
  const filteredReplacementProducts = mockProducts.filter(p => p.name.toLowerCase().includes(replacementSearchQuery.toLowerCase()) || p.sku.toLowerCase().includes(replacementSearchQuery.toLowerCase()));
  const filteredSyncProducts = mockProducts.filter(p => p.name.toLowerCase().includes(syncSearchQuery.toLowerCase()) || p.sku.toLowerCase().includes(syncSearchQuery.toLowerCase()));
  const productsWithPriceMismatch = mockProducts.filter(p => p.hasPriceMismatch);
  // Calculate new price based on handling
  const getNewPrice = (currentPrice: number) => {
    if (operationType === 'sync' && selectedProduct) {
      return selectedProduct.shopifyPrice;
    }
    if (!replacementProduct) return currentPrice;
    if (priceHandling === 'keep-original') return currentPrice;
    if (priceHandling === 'use-new') return replacementProduct.price;
    if (priceHandling === 'custom') return replacementProduct.price * (customPercentage / 100);
    return currentPrice;
  };
  // Handle select/deselect all
  const handleSelectAll = () => {
    setAffectedSubscriptions(prev => prev.map(sub => ({
      ...sub,
      selected: true
    })));
  };
  const handleDeselectAll = () => {
    setAffectedSubscriptions(prev => prev.map(sub => ({
      ...sub,
      selected: false
    })));
  };
  const handleToggleSubscription = (id: string) => {
    setAffectedSubscriptions(prev => prev.map(sub => sub.id === id ? {
      ...sub,
      selected: !sub.selected
    } : sub));
  };
  // Handle execute
  const handleExecute = () => {
    setShowConfirmModal(true);
  };
  const handleConfirmExecute = () => {
    setIsProcessing(true);
    setShowConfirmModal(false);
    // Simulate processing
    setTimeout(() => {
      setIsProcessing(false);
      setShowSuccessBanner(true);
      // Reset form
      setOriginalProduct(null);
      setReplacementProduct(null);
      setSelectedProduct(null);
      setReplacementReason('');
    }, 2000);
  };
  const selectedCount = affectedSubscriptions.filter(s => s.selected).length;
  // Dynamic steps based on operation type
  const steps = operationType === 'replace' ? [{
    num: 1,
    label: 'Select Original'
  }, {
    num: 2,
    label: 'Select Replacement'
  }, {
    num: 3,
    label: 'Review Affected'
  }, {
    num: 4,
    label: 'Execute Replace'
  }] : [{
    num: 1,
    label: 'Select Product'
  }, {
    num: 2,
    label: 'Review Affected'
  }, {
    num: 3,
    label: 'Sync Prices'
  }];
  const activeProduct = operationType === 'sync' ? selectedProduct : originalProduct;
  return <div className="bg-[#F1F5F9] min-h-full p-6 pb-32" data-id="element-8260">
      {/* NavBar */}
      <div className="mb-6" data-id="element-8261">
        <NavBar tabs={[]} title="Product Replace" subtitle="Swap products across multiple subscriptions at once" data-id="element-8262" />
      </div>

      {/* Success Banner */}
      {showSuccessBanner && <div className="mb-6 p-4 bg-[#D1FAE5] border border-[#10B981] rounded-lg flex items-start gap-3" data-id="element-8263">
          <CheckCircle className="w-5 h-5 text-[#065F46] flex-shrink-0 mt-0.5" data-id="element-8264" />
          <div className="flex-1" data-id="element-8265">
            <h3 className="text-[14px] font-semibold text-[#065F46] mb-1" data-id="element-8266">
              {operationType === 'replace' ? 'Product replacement complete' : 'Price sync complete'}
            </h3>
            <p className="text-[14px] text-[#065F46]" data-id="element-8267">
              {selectedCount} subscriptions have been updated. Customers will be
              notified shortly.
            </p>
            <button onClick={() => navigate('/operations/activity-log')} className="text-[14px] font-medium text-[#0EA5E9] hover:text-[#0284C7] mt-2" data-id="element-8268">
              View activity log →
            </button>
          </div>
          <button onClick={() => setShowSuccessBanner(false)} className="p-1 hover:bg-[#10B981] hover:bg-opacity-20 rounded transition-colors" data-id="element-8269">
            <X className="w-5 h-5 text-[#065F46]" data-id="element-8270" />
          </button>
        </div>}

      <div className="max-w-6xl mx-auto space-y-6" data-id="element-8271">
        {/* Section 1: How It Works */}
        <Card data-id="element-8272">
          <button onClick={() => setHowItWorksExpanded(!howItWorksExpanded)} className="w-full flex items-center justify-between" data-id="element-8273">
            <h2 className="text-[20px] font-semibold text-[#0F172A]" data-id="element-8274">
              How{' '}
              {operationType === 'replace' ? 'Product Replace' : 'Price Sync'}{' '}
              Works
            </h2>
            {howItWorksExpanded ? <ChevronUp className="w-5 h-5 text-[#64748B]" data-id="element-8275" /> : <ChevronDown className="w-5 h-5 text-[#64748B]" data-id="element-8276" />}
          </button>

          {howItWorksExpanded && <div className="mt-6" data-id="element-8277">
              {/* Step indicators */}
              <div className="flex items-center justify-between mb-6" data-id="element-8278">
                {steps.map((step, index) => <Fragment key={step.num} data-id="element-8279">
                    <div className="flex flex-col items-center" data-id="element-8280">
                      <div className="w-10 h-10 rounded-full bg-[#0EA5E9] text-white flex items-center justify-center font-semibold mb-2" data-id="element-8281">
                        {step.num}
                      </div>
                      <span className="text-[12px] text-[#64748B] text-center" data-id="element-8282">
                        {step.label}
                      </span>
                    </div>
                    {index < steps.length - 1 && <div className="flex-1 h-0.5 bg-[#E2E8F0] mx-2 mb-6" data-id="element-8283" />}
                  </Fragment>)}
              </div>

              {/* Info banner */}
              <div className="p-4 bg-[#E0F2FE] border border-[#7DD3FC] rounded-md flex items-start gap-3" data-id="element-8284">
                <Info className="w-5 h-5 text-[#0369A1] flex-shrink-0 mt-0.5" data-id="element-8285" />
                <p className="text-[14px] text-[#0369A1]" data-id="element-8286">
                  {operationType === 'replace' ? "Product Replace is commonly used when a wine vintage sells out. Replace '2022 Shiraz' with '2023 Shiraz' across all subscriptions in one action." : "Price Sync updates subscription prices to match your current Shopify prices. Use this when you've changed product prices and want existing subscriptions to reflect the new pricing."}
                </p>
              </div>
            </div>}
        </Card>

        {/* NEW: Operation Type Section */}
        <div className="bg-white rounded-lg border border-[#E2E8F0] shadow-sm p-6" data-id="element-8287">
          <h2 className="text-[20px] font-semibold text-[#0F172A] mb-4" data-id="element-8288">
            Operation Type
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-8289">
            {/* Replace Product Option */}
            <label className={`flex items-start gap-4 p-4 border-2 rounded-lg cursor-pointer transition-all ${operationType === 'replace' ? 'border-[#0EA5E9] bg-[#F0F9FF]' : 'border-[#E2E8F0] hover:border-[#CBD5E1]'}`} data-id="element-8290">
              <input type="radio" name="operationType" value="replace" checked={operationType === 'replace'} onChange={e => setOperationType(e.target.value as 'replace' | 'sync')} className="mt-1 w-5 h-5 text-[#0EA5E9] border-[#CBD5E1] focus:ring-[#0EA5E9]" data-id="element-8291" />
              <div data-id="element-8292">
                <div className="flex items-center gap-2 mb-1" data-id="element-8293">
                  <ArrowRightLeft className="w-5 h-5 text-[#0EA5E9]" data-id="element-8294" />
                  <span className="text-[16px] font-semibold text-[#0F172A]" data-id="element-8295">
                    Replace Product
                  </span>
                </div>
                <p className="text-[14px] text-[#64748B]" data-id="element-8296">
                  Swap one product for another across subscriptions
                </p>
                <p className="text-[12px] text-[#94A3B8] mt-1" data-id="element-8297">
                  e.g., Replace sold-out 2022 vintage with 2023 vintage
                </p>
              </div>
            </label>

            {/* Sync Prices Option */}
            <label className={`flex items-start gap-4 p-4 border-2 rounded-lg cursor-pointer transition-all ${operationType === 'sync' ? 'border-[#0EA5E9] bg-[#F0F9FF]' : 'border-[#E2E8F0] hover:border-[#CBD5E1]'}`} data-id="element-8298">
              <input type="radio" name="operationType" value="sync" checked={operationType === 'sync'} onChange={e => setOperationType(e.target.value as 'replace' | 'sync')} className="mt-1 w-5 h-5 text-[#0EA5E9] border-[#CBD5E1] focus:ring-[#0EA5E9]" data-id="element-8299" />
              <div data-id="element-8300">
                <div className="flex items-center gap-2 mb-1" data-id="element-8301">
                  <RefreshCw className="w-5 h-5 text-[#0EA5E9]" data-id="element-8302" />
                  <span className="text-[16px] font-semibold text-[#0F172A]" data-id="element-8303">
                    Sync Prices
                  </span>
                </div>
                <p className="text-[14px] text-[#64748B]" data-id="element-8304">
                  Update subscription prices to match current Shopify prices
                </p>
                <p className="text-[12px] text-[#94A3B8] mt-1" data-id="element-8305">
                  e.g., Apply recent price changes to existing subscriptions
                </p>
              </div>
            </label>
          </div>
        </div>

        {/* Section 2: Product Selection - Conditional based on operation type */}
        {operationType === 'replace' ? <Card data-id="element-8306">
            <h2 className="text-[20px] font-semibold text-[#0F172A] mb-6" data-id="element-8307">
              Select Products
            </h2>

            <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto_1fr] gap-6" data-id="element-8308">
              {/* Original Product */}
              <div data-id="element-8309">
                <h3 className="text-[16px] font-semibold text-[#0F172A] mb-1" data-id="element-8310">
                  Original Product
                </h3>
                <p className="text-[13px] text-[#64748B] mb-4" data-id="element-8311">
                  The product currently in subscriptions that you want to
                  replace
                </p>

                {/* Search */}
                <div className="relative mb-4" data-id="element-8312">
                  <input type="text" placeholder="🔍 Search products..." value={originalSearchQuery} onChange={e => {
                setOriginalSearchQuery(e.target.value);
                setShowOriginalDropdown(true);
              }} onFocus={() => setShowOriginalDropdown(true)} className="w-full h-[40px] px-4 border border-[#CBD5E1] rounded-md text-[14px] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent" data-id="element-8313" />

                  {/* Dropdown */}
                  {showOriginalDropdown && originalSearchQuery && <>
                      <div className="fixed inset-0 z-10" onClick={() => setShowOriginalDropdown(false)} data-id="element-8314" />
                      <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-[#CBD5E1] rounded-md shadow-lg z-20 max-h-64 overflow-y-auto" data-id="element-8315">
                        {filteredOriginalProducts.map(product => <button key={product.id} onClick={() => {
                    setOriginalProduct(product);
                    setOriginalSearchQuery('');
                    setShowOriginalDropdown(false);
                  }} className="w-full p-3 hover:bg-[#F8FAFC] transition-colors text-left flex items-center gap-3 border-b border-[#E2E8F0] last:border-0" data-id="element-8316">
                            <span className="text-2xl" data-id="element-8317">{product.image}</span>
                            <div className="flex-1" data-id="element-8318">
                              <div className="text-[14px] font-medium text-[#0F172A]" data-id="element-8319">
                                {product.name}
                              </div>
                              <div className="text-[12px] text-[#94A3B8]" data-id="element-8320">
                                {product.sku} · ${product.price.toFixed(2)}
                              </div>
                            </div>
                            {product.stock < 20 && <Badge variant="warning" data-id="element-8321">Low stock</Badge>}
                          </button>)}
                      </div>
                    </>}
                </div>

                {/* Selected or Empty State */}
                {originalProduct ? <div className="p-4 border-2 border-[#0EA5E9] bg-[#F0F9FF] rounded-lg" data-id="element-8322">
                    <div className="flex items-start justify-between mb-3" data-id="element-8323">
                      <div className="flex items-start gap-3" data-id="element-8324">
                        <span className="text-3xl" data-id="element-8325">
                          {originalProduct.image}
                        </span>
                        <div data-id="element-8326">
                          <h4 className="text-[16px] font-semibold text-[#0F172A]" data-id="element-8327">
                            {originalProduct.name}
                          </h4>
                          <p className="text-[13px] text-[#64748B]" data-id="element-8328">
                            750ml · SKU: {originalProduct.sku}
                          </p>
                          <p className="text-[14px] font-semibold text-[#0F172A] mt-1" data-id="element-8329">
                            ${originalProduct.price.toFixed(2)}
                          </p>
                        </div>
                      </div>
                      <button onClick={() => setOriginalProduct(null)} className="p-1 hover:bg-[#0EA5E9] hover:bg-opacity-20 rounded transition-colors" data-id="element-8330">
                        <X className="w-5 h-5 text-[#0EA5E9]" data-id="element-8331" />
                      </button>
                    </div>
                    <div className="space-y-2 pt-3 border-t border-[#7DD3FC]" data-id="element-8332">
                      <div className="flex items-center gap-2 text-[13px] text-[#0369A1]" data-id="element-8333">
                        <span data-id="element-8334">📊</span>
                        <span data-id="element-8335">
                          In {originalProduct.subscriptionCount} active
                          subscriptions
                        </span>
                      </div>
                      {originalProduct.stock < 20 && <div className="flex items-center gap-2 text-[13px] text-[#92400E]" data-id="element-8336">
                          <AlertTriangle className="w-4 h-4" data-id="element-8337" />
                          <span data-id="element-8338">
                            Low stock: {originalProduct.stock} remaining
                          </span>
                        </div>}
                    </div>
                  </div> : <div className="p-8 border-2 border-dashed border-[#CBD5E1] rounded-lg text-center" data-id="element-8339">
                    <Package className="w-12 h-12 text-[#CBD5E1] mx-auto mb-3" data-id="element-8340" />
                    <p className="text-[14px] text-[#94A3B8]" data-id="element-8341">
                      Select a product to replace
                    </p>
                    <p className="text-[13px] text-[#CBD5E1] mt-1" data-id="element-8342">
                      Search above or browse your catalog
                    </p>
                  </div>}

                {/* Subscription Summary Panel */}
                {originalProduct && subscriptionSummaryData[originalProduct.sku] && <div className="mt-4 bg-white border border-[#E2E8F0] rounded-lg shadow-sm overflow-hidden animate-in fade-in slide-in-from-top-2 duration-300" data-id="element-8343">
                      <div className="px-4 py-3 bg-[#F8FAFC] border-b border-[#E2E8F0]" data-id="element-8344">
                        <h4 className="text-[14px] font-semibold text-[#0F172A]" data-id="element-8345">
                          Subscriptions containing {originalProduct.name} (
                          {subscriptionSummaryData[originalProduct.sku].total})
                        </h4>
                      </div>
                      <div className="p-4 space-y-4" data-id="element-8346">
                        {/* Status Breakdown */}
                        <div className="flex flex-wrap gap-2" data-id="element-8347">
                          <Badge variant="success" data-id="element-8348">
                            {subscriptionSummaryData[originalProduct.sku].active}{' '}
                            Active
                          </Badge>
                          <Badge variant="warning" data-id="element-8349">
                            {subscriptionSummaryData[originalProduct.sku].paused}{' '}
                            Paused
                          </Badge>
                          <Badge variant="error" data-id="element-8350">
                            {subscriptionSummaryData[originalProduct.sku].failedPayment}{' '}
                            Failed Payment
                          </Badge>
                        </div>

                        {/* Key Metrics */}
                        <div className="grid grid-cols-2 gap-4 pt-2" data-id="element-8351">
                          <div data-id="element-8352">
                            <p className="text-[12px] font-medium text-[#64748B] uppercase tracking-wide" data-id="element-8353">
                              Next Billing
                            </p>
                            <p className="text-[14px] font-medium text-[#0F172A]" data-id="element-8354">
                              {subscriptionSummaryData[originalProduct.sku].nextBilling}
                            </p>
                          </div>
                          <div data-id="element-8355">
                            <p className="text-[12px] font-medium text-[#64748B] uppercase tracking-wide" data-id="element-8356">
                              Total Units
                            </p>
                            <p className="text-[14px] font-medium text-[#0F172A]" data-id="element-8357">
                              {subscriptionSummaryData[originalProduct.sku].totalUnits}
                            </p>
                          </div>
                        </div>

                        {/* Plans */}
                        <div data-id="element-8358">
                          <p className="text-[12px] font-medium text-[#64748B] uppercase tracking-wide mb-1" data-id="element-8359">
                            Plans
                          </p>
                          <p className="text-[13px] text-[#475569] leading-relaxed" data-id="element-8360">
                            {subscriptionSummaryData[originalProduct.sku].plans.map((plan, i, arr) => <span key={plan.name} data-id="element-8361">
                                {plan.name}{' '}
                                <span className="text-[#94A3B8]" data-id="element-8362">
                                  ({plan.count})
                                </span>
                                {i < arr.length - 1 ? ', ' : ''}
                              </span>)}
                          </p>
                        </div>
                      </div>
                    </div>}
              </div>

              {/* Arrow */}
              <div className="hidden lg:flex items-center justify-center" data-id="element-8363">
                <ArrowRight className="w-8 h-8 text-[#94A3B8]" data-id="element-8364" />
              </div>

              {/* Replacement Product */}
              <div data-id="element-8365">
                <h3 className="text-[16px] font-semibold text-[#0F172A] mb-1" data-id="element-8366">
                  Replacement Product
                </h3>
                <p className="text-[13px] text-[#64748B] mb-4" data-id="element-8367">
                  The product that will replace the original
                </p>

                {/* Search */}
                <div className="relative mb-4" data-id="element-8368">
                  <input type="text" placeholder="🔍 Search products..." value={replacementSearchQuery} onChange={e => {
                setReplacementSearchQuery(e.target.value);
                setShowReplacementDropdown(true);
              }} onFocus={() => setShowReplacementDropdown(true)} className="w-full h-[40px] px-4 border border-[#CBD5E1] rounded-md text-[14px] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent" data-id="element-8369" />

                  {/* Dropdown */}
                  {showReplacementDropdown && replacementSearchQuery && <>
                      <div className="fixed inset-0 z-10" onClick={() => setShowReplacementDropdown(false)} data-id="element-8370" />
                      <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-[#CBD5E1] rounded-md shadow-lg z-20 max-h-64 overflow-y-auto" data-id="element-8371">
                        {filteredReplacementProducts.map(product => <button key={product.id} onClick={() => {
                    setReplacementProduct(product);
                    setReplacementSearchQuery('');
                    setShowReplacementDropdown(false);
                  }} className="w-full p-3 hover:bg-[#F8FAFC] transition-colors text-left flex items-center gap-3 border-b border-[#E2E8F0] last:border-0" data-id="element-8372">
                            <span className="text-2xl" data-id="element-8373">{product.image}</span>
                            <div className="flex-1" data-id="element-8374">
                              <div className="text-[14px] font-medium text-[#0F172A]" data-id="element-8375">
                                {product.name}
                              </div>
                              <div className="text-[12px] text-[#94A3B8]" data-id="element-8376">
                                {product.sku} · ${product.price.toFixed(2)}
                              </div>
                            </div>
                            {product.stock > 50 && <Badge variant="success" data-id="element-8377">In stock</Badge>}
                          </button>)}
                      </div>
                    </>}
                </div>

                {/* Selected or Empty State */}
                {replacementProduct ? <div className="p-4 border-2 border-[#0EA5E9] bg-[#F0F9FF] rounded-lg" data-id="element-8378">
                    <div className="flex items-start justify-between mb-3" data-id="element-8379">
                      <div className="flex items-start gap-3" data-id="element-8380">
                        <span className="text-3xl" data-id="element-8381">
                          {replacementProduct.image}
                        </span>
                        <div data-id="element-8382">
                          <h4 className="text-[16px] font-semibold text-[#0F172A]" data-id="element-8383">
                            {replacementProduct.name}
                          </h4>
                          <p className="text-[13px] text-[#64748B]" data-id="element-8384">
                            750ml · SKU: {replacementProduct.sku}
                          </p>
                          <p className="text-[14px] font-semibold text-[#0F172A] mt-1" data-id="element-8385">
                            ${replacementProduct.price.toFixed(2)}
                          </p>
                        </div>
                      </div>
                      <button onClick={() => setReplacementProduct(null)} className="p-1 hover:bg-[#0EA5E9] hover:bg-opacity-20 rounded transition-colors" data-id="element-8386">
                        <X className="w-5 h-5 text-[#0EA5E9]" data-id="element-8387" />
                      </button>
                    </div>
                    <div className="pt-3 border-t border-[#7DD3FC]" data-id="element-8388">
                      <div className="flex items-center gap-2 text-[13px] text-[#059669]" data-id="element-8389">
                        <CheckCircle className="w-4 h-4" data-id="element-8390" />
                        <span data-id="element-8391">
                          In stock: {replacementProduct.stock} available
                        </span>
                      </div>
                    </div>
                  </div> : <div className="p-8 border-2 border-dashed border-[#CBD5E1] rounded-lg text-center" data-id="element-8392">
                    <Package className="w-12 h-12 text-[#CBD5E1] mx-auto mb-3" data-id="element-8393" />
                    <p className="text-[14px] text-[#94A3B8]" data-id="element-8394">
                      Select a replacement product
                    </p>
                    <p className="text-[13px] text-[#CBD5E1] mt-1" data-id="element-8395">
                      Search above or browse your catalog
                    </p>
                  </div>}
              </div>
            </div>

            {/* Same Product Prompt */}
            {showSameProductPrompt && <div className="mt-6 bg-[#F0F9FF] border border-[#0EA5E9] rounded-lg p-4 flex items-center justify-between" data-id="element-8396">
                <div className="flex items-center gap-3" data-id="element-8397">
                  <Lightbulb className="w-5 h-5 text-[#0EA5E9]" data-id="element-8398" />
                  <div data-id="element-8399">
                    <p className="text-[14px] font-medium text-[#0F172A]" data-id="element-8400">
                      Looks like you want to sync prices
                    </p>
                    <p className="text-[13px] text-[#64748B]" data-id="element-8401">
                      You've selected the same product for both. Switch to Price
                      Sync mode?
                    </p>
                  </div>
                </div>
                <div className="flex gap-2" data-id="element-8402">
                  <button onClick={() => {
              setOperationType('sync');
              setSelectedProduct(originalProduct);
              setOriginalProduct(null);
              setReplacementProduct(null);
            }} className="px-4 py-2 bg-[#0EA5E9] text-white text-[14px] font-medium rounded-md hover:bg-[#0284C7]" data-id="element-8403">
                    Switch to Price Sync
                  </button>
                  <button onClick={() => setShowSameProductPrompt(false)} className="px-4 py-2 text-[14px] text-[#64748B] hover:text-[#0F172A]" data-id="element-8404">
                    Continue
                  </button>
                </div>
              </div>}

            {/* Price Difference Warning */}
            {originalProduct && replacementProduct && hasPriceDifference && !showSameProductPrompt && <div className="mt-6 p-4 bg-[#FEF3C7] border border-[#FDE68A] rounded-md flex items-start gap-3" data-id="element-8405">
                  <AlertTriangle className="w-5 h-5 text-[#92400E] flex-shrink-0 mt-0.5" data-id="element-8406" />
                  <p className="text-[14px] text-[#92400E]" data-id="element-8407">
                    <span className="font-semibold" data-id="element-8408">Price difference:</span> The
                    replacement product is $
                    {Math.abs(priceDifference).toFixed(2)}{' '}
                    {priceDifference > 0 ? 'more expensive' : 'cheaper'}. Choose
                    how to handle this below.
                  </p>
                </div>}
          </Card> /* SYNC MODE - Single Product Selection */ : <div className="bg-white rounded-lg border border-[#E2E8F0] shadow-sm p-6" data-id="element-8409">
            <h2 className="text-[20px] font-semibold text-[#0F172A] mb-2" data-id="element-8410">
              Select Product
            </h2>
            <p className="text-[14px] text-[#64748B] mb-6" data-id="element-8411">
              Choose a product to sync prices for
            </p>

            {/* Single product search */}
            <div className="relative mb-6" data-id="element-8412">
              <input type="text" placeholder="🔍 Search products..." value={syncSearchQuery} onChange={e => {
            setSyncSearchQuery(e.target.value);
            setShowSyncDropdown(true);
          }} onFocus={() => setShowSyncDropdown(true)} className="w-full h-[40px] px-4 border border-[#CBD5E1] rounded-md text-[14px] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent" data-id="element-8413" />

              {/* Dropdown */}
              {showSyncDropdown && syncSearchQuery && <>
                  <div className="fixed inset-0 z-10" onClick={() => setShowSyncDropdown(false)} data-id="element-8414" />
                  <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-[#CBD5E1] rounded-md shadow-lg z-20 max-h-64 overflow-y-auto" data-id="element-8415">
                    {filteredSyncProducts.map(product => <button key={product.id} onClick={() => {
                setSelectedProduct(product);
                setSyncSearchQuery('');
                setShowSyncDropdown(false);
              }} className="w-full p-3 hover:bg-[#F8FAFC] transition-colors text-left flex items-center gap-3 border-b border-[#E2E8F0] last:border-0" data-id="element-8416">
                        <span className="text-2xl" data-id="element-8417">{product.image}</span>
                        <div className="flex-1" data-id="element-8418">
                          <div className="text-[14px] font-medium text-[#0F172A]" data-id="element-8419">
                            {product.name}
                          </div>
                          <div className="text-[12px] text-[#94A3B8]" data-id="element-8420">
                            {product.sku} · ${product.price.toFixed(2)}
                          </div>
                        </div>
                      </button>)}
                  </div>
                </>}
            </div>

            {/* Selected Product */}
            {selectedProduct && <div className="p-4 border-2 border-[#0EA5E9] bg-[#F0F9FF] rounded-lg mb-6" data-id="element-8421">
                <div className="flex items-start justify-between" data-id="element-8422">
                  <div className="flex items-start gap-3" data-id="element-8423">
                    <span className="text-3xl" data-id="element-8424">{selectedProduct.image}</span>
                    <div data-id="element-8425">
                      <h4 className="text-[16px] font-semibold text-[#0F172A]" data-id="element-8426">
                        {selectedProduct.name}
                      </h4>
                      <p className="text-[13px] text-[#64748B]" data-id="element-8427">
                        750ml · SKU: {selectedProduct.sku}
                      </p>
                      <p className="text-[14px] font-semibold text-[#0F172A] mt-1" data-id="element-8428">
                        Shopify Price: $
                        {selectedProduct.shopifyPrice.toFixed(2)}
                      </p>
                    </div>
                  </div>
                  <button onClick={() => setSelectedProduct(null)} className="p-1 hover:bg-[#0EA5E9] hover:bg-opacity-20 rounded transition-colors" data-id="element-8429">
                    <X className="w-5 h-5 text-[#0EA5E9]" data-id="element-8430" />
                  </button>
                </div>
              </div>}

            {/* Products with Price Differences - Quick Select */}
            {productsWithPriceMismatch.length > 0 && <div className="border-t border-[#E2E8F0] pt-6" data-id="element-8431">
                <div className="flex items-center gap-2 mb-4" data-id="element-8432">
                  <AlertTriangle className="w-5 h-5 text-[#F59E0B]" data-id="element-8433" />
                  <span className="text-[14px] font-medium text-[#0F172A]" data-id="element-8434">
                    {productsWithPriceMismatch.length} products have price
                    differences
                  </span>
                </div>

                <div className="space-y-3" data-id="element-8435">
                  {productsWithPriceMismatch.map(product => <button key={product.id} onClick={() => setSelectedProduct(product)} className="w-full flex items-center justify-between p-4 border border-[#E2E8F0] rounded-lg hover:bg-[#F8FAFC] cursor-pointer transition-colors" data-id="element-8436">
                      <div className="flex items-center gap-4" data-id="element-8437">
                        <div className="w-12 h-12 bg-[#F1F5F9] rounded-md flex items-center justify-center text-2xl" data-id="element-8438">
                          {product.image}
                        </div>
                        <div className="text-left" data-id="element-8439">
                          <p className="text-[14px] font-medium text-[#0F172A]" data-id="element-8440">
                            {product.name}
                          </p>
                          <p className="text-[13px] text-[#64748B]" data-id="element-8441">
                            {product.subscriptionCount} subscriptions affected
                          </p>
                        </div>
                      </div>
                      <div className="text-right" data-id="element-8442">
                        <p className="text-[14px] text-[#64748B]" data-id="element-8443">
                          <span className="line-through" data-id="element-8444">
                            ${product.subscriptionPrice?.toFixed(2)}
                          </span>
                          <span className="mx-2" data-id="element-8445">→</span>
                          <span className="font-semibold text-[#0F172A]" data-id="element-8446">
                            ${product.shopifyPrice.toFixed(2)}
                          </span>
                        </p>
                        <p className="text-[13px] text-[#F59E0B]" data-id="element-8447">
                          +$
                          {(product.shopifyPrice - (product.subscriptionPrice || 0)).toFixed(2)}
                        </p>
                      </div>
                    </button>)}
                </div>

                {/* Sync All Button */}
                <button className="mt-4 w-full h-[48px] flex items-center justify-center gap-2 border-2 border-dashed border-[#CBD5E1] rounded-lg text-[14px] font-medium text-[#0EA5E9] hover:bg-[#F0F9FF] hover:border-[#0EA5E9] transition-all" data-id="element-8448">
                  <RefreshCw className="w-4 h-4" data-id="element-8449" />
                  Sync All Products with Price Differences
                </button>
              </div>}
          </div>}

        {/* Price Comparison Section (Sync Mode Only) */}
        {operationType === 'sync' && selectedProduct && <div className="bg-white rounded-lg border border-[#E2E8F0] shadow-sm p-6" data-id="element-8450">
            <h2 className="text-[20px] font-semibold text-[#0F172A] mb-6" data-id="element-8451">
              Price Comparison
            </h2>

            {/* Visual comparison */}
            <div className="flex items-center justify-center gap-8 py-8 bg-[#F8FAFC] rounded-lg mb-6" data-id="element-8452">
              <div className="text-center" data-id="element-8453">
                <p className="text-[12px] font-medium text-[#64748B] uppercase tracking-wide mb-2" data-id="element-8454">
                  Subscription Price
                </p>
                <div className="bg-white border border-[#E2E8F0] rounded-lg px-8 py-4" data-id="element-8455">
                  <p className="text-[28px] font-semibold text-[#64748B]" data-id="element-8456">
                    $
                    {(selectedProduct.subscriptionPrice || selectedProduct.price).toFixed(2)}
                  </p>
                  <p className="text-[12px] text-[#94A3B8]" data-id="element-8457">current</p>
                </div>
              </div>

              <ArrowRight className="w-8 h-8 text-[#CBD5E1]" data-id="element-8458" />

              <div className="text-center" data-id="element-8459">
                <p className="text-[12px] font-medium text-[#64748B] uppercase tracking-wide mb-2" data-id="element-8460">
                  Shopify Price
                </p>
                <div className="bg-white border-2 border-[#0EA5E9] rounded-lg px-8 py-4" data-id="element-8461">
                  <p className="text-[28px] font-semibold text-[#0F172A]" data-id="element-8462">
                    ${selectedProduct.shopifyPrice.toFixed(2)}
                  </p>
                  <p className="text-[12px] text-[#0EA5E9]" data-id="element-8463">new</p>
                </div>
              </div>
            </div>

            {/* Impact summary */}
            <div className="grid grid-cols-3 gap-4 text-center" data-id="element-8464">
              <div className="p-4 bg-[#F8FAFC] rounded-lg" data-id="element-8465">
                <p className="text-[24px] font-semibold text-[#0F172A]" data-id="element-8466">
                  +$
                  {(selectedProduct.shopifyPrice - (selectedProduct.subscriptionPrice || selectedProduct.price)).toFixed(2)}
                </p>
                <p className="text-[13px] text-[#64748B]" data-id="element-8467">
                  per unit (+
                  {((selectedProduct.shopifyPrice - (selectedProduct.subscriptionPrice || selectedProduct.price)) / (selectedProduct.subscriptionPrice || selectedProduct.price) * 100).toFixed(1)}
                  %)
                </p>
              </div>
              <div className="p-4 bg-[#F8FAFC] rounded-lg" data-id="element-8468">
                <p className="text-[24px] font-semibold text-[#0F172A]" data-id="element-8469">
                  {selectedProduct.subscriptionCount}
                </p>
                <p className="text-[13px] text-[#64748B]" data-id="element-8470">subscriptions</p>
              </div>
              <div className="p-4 bg-[#F8FAFC] rounded-lg" data-id="element-8471">
                <p className="text-[24px] font-semibold text-[#10B981]" data-id="element-8472">
                  +$
                  {((selectedProduct.shopifyPrice - (selectedProduct.subscriptionPrice || selectedProduct.price)) * 28).toFixed(2)}
                </p>
                <p className="text-[13px] text-[#64748B]" data-id="element-8473">revenue/cycle</p>
              </div>
            </div>
          </div>}

        {/* Section 3: Replace Options */}
        {originalProduct && replacementProduct && <Card data-id="element-8474">
            <h2 className="text-[20px] font-semibold text-[#0F172A] mb-6" data-id="element-8475">
              Replace Options
            </h2>

            {/* Price Handling - Only show in replace mode when prices differ */}
            {operationType === 'replace' && hasPriceDifference && <div className="mb-8" data-id="element-8476">
                <h3 className="text-[14px] font-semibold text-[#475569] uppercase tracking-wider mb-4" data-id="element-8477">
                  Price Handling
                </h3>
                <div className="space-y-3" data-id="element-8478">
                  <label className="flex items-start gap-3 cursor-pointer" data-id="element-8479">
                    <input type="radio" name="priceHandling" value="keep-original" checked={priceHandling === 'keep-original'} onChange={e => setPriceHandling(e.target.value)} className="mt-1 w-5 h-5 text-[#0EA5E9] border-[#CBD5E1] focus:ring-[#0EA5E9]" data-id="element-8480" />
                    <div data-id="element-8481">
                      <div className="text-[14px] font-medium text-[#0F172A]" data-id="element-8482">
                        Keep original price - Subscribers continue paying $
                        {originalProduct.price.toFixed(2)}
                      </div>
                      <div className="text-[13px] text-[#64748B] mt-1" data-id="element-8483">
                        Subscriber's price won't change. You absorb the
                        difference.
                      </div>
                    </div>
                  </label>

                  <label className="flex items-start gap-3 cursor-pointer" data-id="element-8484">
                    <input type="radio" name="priceHandling" value="use-new" checked={priceHandling === 'use-new'} onChange={e => setPriceHandling(e.target.value)} className="mt-1 w-5 h-5 text-[#0EA5E9] border-[#CBD5E1] focus:ring-[#0EA5E9]" data-id="element-8485" />
                    <div data-id="element-8486">
                      <div className="text-[14px] font-medium text-[#0F172A]" data-id="element-8487">
                        Use new product price - Subscribers will pay $
                        {replacementProduct.price.toFixed(2)}
                      </div>
                      <div className="text-[13px] text-[#64748B] mt-1" data-id="element-8488">
                        Subscriber's price updates to match the new product.
                      </div>
                    </div>
                  </label>

                  <label className="flex items-start gap-3 cursor-pointer" data-id="element-8489">
                    <input type="radio" name="priceHandling" value="custom" checked={priceHandling === 'custom'} onChange={e => setPriceHandling(e.target.value)} className="mt-1 w-5 h-5 text-[#0EA5E9] border-[#CBD5E1] focus:ring-[#0EA5E9]" data-id="element-8490" />
                    <div className="flex-1" data-id="element-8491">
                      <div className="text-[14px] font-medium text-[#0F172A] mb-2" data-id="element-8492">
                        Apply percentage adjustment - Custom pricing
                      </div>
                      {priceHandling === 'custom' && <div className="flex items-center gap-2 mb-2" data-id="element-8493">
                          <input type="number" value={customPercentage} onChange={e => setCustomPercentage(parseInt(e.target.value) || 0)} min="0" max="200" className="w-20 h-[32px] px-2 border border-[#CBD5E1] rounded-md text-[14px] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9]" data-id="element-8494" />
                          <span className="text-[14px] text-[#64748B]" data-id="element-8495">
                            % of new price = $
                            {(replacementProduct.price * (customPercentage / 100)).toFixed(2)}
                          </span>
                        </div>}
                      <div className="text-[13px] text-[#64748B]" data-id="element-8496">
                        e.g., 95% = $
                        {(replacementProduct.price * 0.95).toFixed(2)}
                      </div>
                    </div>
                  </label>
                </div>
              </div>}

            {/* Notification Options */}
            <div className="mb-8" data-id="element-8497">
              <h3 className="text-[14px] font-semibold text-[#475569] uppercase tracking-wider mb-4" data-id="element-8498">
                Notification Options
              </h3>
              <div className="space-y-6" data-id="element-8499">
                <div className="flex items-start justify-between" data-id="element-8500">
                  <div className="flex-1 pr-6" data-id="element-8501">
                    <h4 className="text-[16px] font-semibold text-[#0F172A] mb-1" data-id="element-8502">
                      Notify customers of the change
                    </h4>
                    <p className="text-[14px] text-[#64748B]" data-id="element-8503">
                      Send an email letting subscribers know their product has
                      been updated
                    </p>
                    {notifyCustomers && <button className="text-[14px] font-medium text-[#0EA5E9] hover:text-[#0284C7] mt-2" data-id="element-8504">
                        Preview email →
                      </button>}
                  </div>
                  <button onClick={() => setNotifyCustomers(!notifyCustomers)} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:ring-offset-2 flex-shrink-0 ${notifyCustomers ? 'bg-[#0EA5E9]' : 'bg-[#CBD5E1]'}`} data-id="element-8505">
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${notifyCustomers ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-8506" />
                  </button>
                </div>

                {notifyCustomers && <div className="flex items-start justify-between pl-6 border-l-2 border-[#E2E8F0]" data-id="element-8507">
                    <div className="flex-1 pr-6" data-id="element-8508">
                      <h4 className="text-[16px] font-semibold text-[#0F172A] mb-1" data-id="element-8509">
                        Include reason in notification
                      </h4>
                      <p className="text-[14px] text-[#64748B] mb-3" data-id="element-8510">
                        Explain why the product is being replaced
                      </p>
                      {includeReason && <div data-id="element-8511">
                          <label className="block text-[12px] font-medium text-[#475569] mb-2" data-id="element-8512">
                            Reason for replacement
                          </label>
                          <textarea value={replacementReason} onChange={e => setReplacementReason(e.target.value)} placeholder="e.g., The 2022 vintage has sold out. We've upgraded you to our new 2023 vintage at no extra cost!" maxLength={500} rows={3} className="w-full px-3 py-2 border border-[#CBD5E1] rounded-md text-[14px] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent resize-none" data-id="element-8513" />
                          <div className="text-[12px] text-[#94A3B8] mt-1 text-right" data-id="element-8514">
                            {replacementReason.length}/500
                          </div>
                        </div>}
                    </div>
                    <button onClick={() => setIncludeReason(!includeReason)} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:ring-offset-2 flex-shrink-0 ${includeReason ? 'bg-[#0EA5E9]' : 'bg-[#CBD5E1]'}`} data-id="element-8515">
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${includeReason ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-8516" />
                    </button>
                  </div>}
              </div>
            </div>

            {/* Timing Options */}
            <div data-id="element-8517">
              <h3 className="text-[14px] font-semibold text-[#475569] uppercase tracking-wider mb-4" data-id="element-8518">
                Timing Options
              </h3>
              <div className="space-y-3" data-id="element-8519">
                <label className="flex items-start gap-3 cursor-pointer" data-id="element-8520">
                  <input type="radio" name="timing" value="immediate" checked={timing === 'immediate'} onChange={e => setTiming(e.target.value)} className="mt-1 w-5 h-5 text-[#0EA5E9] border-[#CBD5E1] focus:ring-[#0EA5E9]" data-id="element-8521" />
                  <div data-id="element-8522">
                    <div className="text-[14px] font-medium text-[#0F172A]" data-id="element-8523">
                      Replace immediately
                    </div>
                    <div className="text-[13px] text-[#64748B] mt-1" data-id="element-8524">
                      Changes take effect now
                    </div>
                  </div>
                </label>

                <label className="flex items-start gap-3 cursor-pointer" data-id="element-8525">
                  <input type="radio" name="timing" value="next-order" checked={timing === 'next-order'} onChange={e => setTiming(e.target.value)} className="mt-1 w-5 h-5 text-[#0EA5E9] border-[#CBD5E1] focus:ring-[#0EA5E9]" data-id="element-8526" />
                  <div data-id="element-8527">
                    <div className="text-[14px] font-medium text-[#0F172A]" data-id="element-8528">
                      Replace on next order
                    </div>
                    <div className="text-[13px] text-[#64748B] mt-1" data-id="element-8529">
                      Changes apply when next order processes
                    </div>
                  </div>
                </label>

                <label className="flex items-start gap-3 cursor-pointer" data-id="element-8530">
                  <input type="radio" name="timing" value="scheduled" checked={timing === 'scheduled'} onChange={e => setTiming(e.target.value)} className="mt-1 w-5 h-5 text-[#0EA5E9] border-[#CBD5E1] focus:ring-[#0EA5E9]" data-id="element-8531" />
                  <div className="flex-1" data-id="element-8532">
                    <div className="text-[14px] font-medium text-[#0F172A] mb-2" data-id="element-8533">
                      Schedule replacement
                    </div>
                    {timing === 'scheduled' && <input type="date" value={scheduledDate} onChange={e => setScheduledDate(e.target.value)} className="w-48 h-[40px] px-3 border border-[#CBD5E1] rounded-md text-[14px] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9]" data-id="element-8534" />}
                  </div>
                </label>
              </div>
            </div>
          </Card>}

        {/* Section 4: Affected Subscriptions */}
        {activeProduct && <Card data-id="element-8535">
            <div className="mb-6" data-id="element-8536">
              <h2 className="text-[20px] font-semibold text-[#0F172A]" data-id="element-8537">
                Affected Subscriptions
              </h2>
              <p className="text-[14px] text-[#64748B] mt-1" data-id="element-8538">
                {selectedCount} subscriptions will be updated
              </p>
            </div>

            {/* Summary */}
            <div className="p-4 bg-[#F8FAFC] rounded-md mb-6 space-y-2" data-id="element-8539">
              <div className="text-[14px] text-[#0F172A]" data-id="element-8540">
                <span className="font-semibold" data-id="element-8541">
                  {activeProduct.subscriptionCount}
                </span>{' '}
                active subscriptions contain {activeProduct.name}
              </div>
              <div className="text-[14px] text-[#0F172A]" data-id="element-8542">
                Total quantity across subscriptions:{' '}
                <span className="font-semibold" data-id="element-8543">47 bottles</span>
              </div>
              <div className="text-[14px] text-[#0F172A]" data-id="element-8544">
                Monthly revenue impact:{' '}
                <span className="font-semibold" data-id="element-8545">
                  $
                  {operationType === 'sync' && selectedProduct ? ((selectedProduct.shopifyPrice - (selectedProduct.subscriptionPrice || selectedProduct.price)) * 47).toFixed(2) : (priceDifference * 47).toFixed(2)}
                </span>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-3 mb-4" data-id="element-8546">
              <Button variant="ghost" size="sm" onClick={handleSelectAll} data-id="element-8547">
                Select All
              </Button>
              <Button variant="ghost" size="sm" onClick={handleDeselectAll} data-id="element-8548">
                Deselect All
              </Button>
            </div>

            {/* Table */}
            <div className="border border-[#E2E8F0] rounded-lg overflow-hidden mb-4" data-id="element-8549">
              <table className="w-full" data-id="element-8550">
                <thead className="bg-[#F1F5F9]" data-id="element-8551">
                  <tr data-id="element-8552">
                    <th className="px-4 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider w-12" data-id="element-8553">
                      Select
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-8554">
                      Subscription
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-8555">
                      Customer
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-8556">
                      Qty
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-8557">
                      Current Price
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-8558">
                      New Price
                    </th>
                    {operationType === 'sync' && <th className="px-4 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-8559">
                        Difference
                      </th>}
                    {operationType === 'replace' && <th className="px-4 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-8560">
                        Next Order
                      </th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E2E8F0]" data-id="element-8561">
                  {affectedSubscriptions.slice(0, showAllSubscriptions ? undefined : 5).map(sub => {
                const newPrice = getNewPrice(sub.currentPrice);
                const difference = newPrice - sub.currentPrice;
                return <tr key={sub.id} className="hover:bg-[#F8FAFC] transition-colors" data-id="element-8562">
                          <td className="px-4 py-3" data-id="element-8563">
                            <input type="checkbox" checked={sub.selected} onChange={() => handleToggleSubscription(sub.id)} className="w-4 h-4 text-[#0EA5E9] border-[#CBD5E1] rounded focus:ring-[#0EA5E9]" data-id="element-8564" />
                          </td>
                          <td className="px-4 py-3" data-id="element-8565">
                            <button className="text-[14px] text-[#0EA5E9] hover:text-[#0284C7] font-medium" data-id="element-8566">
                              {sub.id}
                            </button>
                          </td>
                          <td className="px-4 py-3 text-[14px] text-[#0F172A]" data-id="element-8567">
                            {sub.customer}
                          </td>
                          <td className="px-4 py-3 text-[14px] text-[#475569]" data-id="element-8568">
                            {sub.quantity}
                          </td>
                          <td className="px-4 py-3 text-[14px] text-[#475569]" data-id="element-8569">
                            ${sub.currentPrice.toFixed(2)}
                          </td>
                          <td className="px-4 py-3 text-[14px] font-semibold text-[#0F172A]" data-id="element-8570">
                            ${newPrice.toFixed(2)}
                          </td>
                          {operationType === 'sync' && <td className="px-4 py-3" data-id="element-8571">
                              <span className="text-[14px] font-medium text-[#F59E0B]" data-id="element-8572">
                                +${difference.toFixed(2)}
                              </span>
                            </td>}
                          {operationType === 'replace' && <td className="px-4 py-3 text-[14px] text-[#475569]" data-id="element-8573">
                              {new Date(sub.nextOrder).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric'
                    })}
                            </td>}
                        </tr>;
              })}
                </tbody>
              </table>
            </div>

            {!showAllSubscriptions && affectedSubscriptions.length > 5 && <button onClick={() => setShowAllSubscriptions(true)} className="text-[14px] font-medium text-[#0EA5E9] hover:text-[#0284C7]" data-id="element-8574">
                View all {affectedSubscriptions.length} subscriptions →
              </button>}

            <p className="text-[13px] text-[#64748B] mt-4" data-id="element-8575">
              Deselect any subscriptions you want to exclude from this{' '}
              {operationType === 'replace' ? 'replacement' : 'price sync'}.
            </p>
          </Card>}

        {/* Section 6: Recent Replacements */}
        <Card data-id="element-8576">
          <button onClick={() => setRecentReplacementsExpanded(!recentReplacementsExpanded)} className="w-full flex items-center justify-between" data-id="element-8577">
            <h2 className="text-[20px] font-semibold text-[#0F172A]" data-id="element-8578">
              Recent{' '}
              {operationType === 'replace' ? 'Replacements' : 'Price Syncs'}
            </h2>
            {recentReplacementsExpanded ? <ChevronUp className="w-5 h-5 text-[#64748B]" data-id="element-8579" /> : <ChevronDown className="w-5 h-5 text-[#64748B]" data-id="element-8580" />}
          </button>

          {recentReplacementsExpanded && <div className="mt-6" data-id="element-8581">
              <div className="border border-[#E2E8F0] rounded-lg overflow-hidden" data-id="element-8582">
                <table className="w-full" data-id="element-8583">
                  <thead className="bg-[#F1F5F9]" data-id="element-8584">
                    <tr data-id="element-8585">
                      <th className="px-4 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-8586">
                        Date
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-8587">
                        Original
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-8588">
                        Replacement
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-8589">
                        Subscriptions
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-8590">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#E2E8F0]" data-id="element-8591">
                    {[{
                  date: 'Jan 5, 2026',
                  original: 'Chardonnay 2021',
                  replacement: 'Chardonnay 2022',
                  count: 15
                }, {
                  date: 'Dec 28, 2025',
                  original: 'Pinot Noir 750ml',
                  replacement: 'Pinot Noir 750ml (New Label)',
                  count: 8
                }, {
                  date: 'Dec 15, 2025',
                  original: 'Holiday Gift Box',
                  replacement: 'Winter Collection Box',
                  count: 31
                }].map((item, index) => <tr key={index} className="hover:bg-[#F8FAFC] transition-colors" data-id="element-8592">
                        <td className="px-4 py-3 text-[14px] text-[#475569]" data-id="element-8593">
                          {item.date}
                        </td>
                        <td className="px-4 py-3 text-[14px] text-[#0F172A]" data-id="element-8594">
                          {item.original}
                        </td>
                        <td className="px-4 py-3 text-[14px] text-[#0F172A]" data-id="element-8595">
                          {item.replacement}
                        </td>
                        <td className="px-4 py-3 text-[14px] text-[#475569]" data-id="element-8596">
                          {item.count}
                        </td>
                        <td className="px-4 py-3" data-id="element-8597">
                          <div className="flex items-center gap-2 text-[14px] text-[#059669]" data-id="element-8598">
                            <CheckCircle className="w-4 h-4" data-id="element-8599" />
                            <span data-id="element-8600">Complete</span>
                          </div>
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>
              <button onClick={() => navigate('/operations/activity-log')} className="text-[14px] font-medium text-[#0EA5E9] hover:text-[#0284C7] mt-4" data-id="element-8601">
                View all in Activity Log →
              </button>
            </div>}
        </Card>
      </div>

      {/* Sticky Footer */}
      {(operationType === 'replace' && originalProduct && replacementProduct || operationType === 'sync' && selectedProduct) && <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-[#E2E8F0] shadow-lg z-30" data-id="element-8602">
          <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between" data-id="element-8603">
            <Button variant="outline" onClick={() => {
          setOriginalProduct(null);
          setReplacementProduct(null);
          setSelectedProduct(null);
        }} data-id="element-8604">
              Cancel
            </Button>
            <div className="flex items-center gap-3" data-id="element-8605">
              <Button variant="outline" onClick={() => setShowConfirmModal(true)} data-id="element-8606">
                Preview Changes
              </Button>
              <Button variant="primary" onClick={handleExecute} disabled={selectedCount === 0} data-id="element-8607">
                {operationType === 'replace' ? `Execute Replace (${selectedCount})` : `Sync Prices (${selectedCount})`}
              </Button>
            </div>
          </div>
        </div>}

      {/* Confirmation Modal */}
      {showConfirmModal && (operationType === 'replace' && originalProduct && replacementProduct || operationType === 'sync' && selectedProduct) && <>
            <div className="fixed inset-0 bg-black bg-opacity-50 z-40" onClick={() => setShowConfirmModal(false)} data-id="element-8608" />
            <div className="fixed inset-0 flex items-center justify-center z-50 p-6" data-id="element-8609">
              <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" data-id="element-8610">
                {/* Header */}
                <div className="p-6 border-b border-[#E2E8F0] flex items-center justify-between" data-id="element-8611">
                  <h2 className="text-[20px] font-semibold text-[#0F172A]" data-id="element-8612">
                    Confirm{' '}
                    {operationType === 'replace' ? 'Product Replacement' : 'Price Sync'}
                  </h2>
                  <button onClick={() => setShowConfirmModal(false)} className="p-2 hover:bg-[#F1F5F9] rounded-md transition-colors" data-id="element-8613">
                    <X className="w-5 h-5 text-[#64748B]" data-id="element-8614" />
                  </button>
                </div>

                {/* Body */}
                <div className="p-6 space-y-6" data-id="element-8615">
                  {/* Summary */}
                  <div className="space-y-4" data-id="element-8616">
                    {operationType === 'replace' && originalProduct && replacementProduct ? <div className="flex items-center gap-4" data-id="element-8617">
                        <div className="flex-1 p-3 bg-[#F8FAFC] rounded-md" data-id="element-8618">
                          <div className="text-[12px] text-[#94A3B8] mb-1" data-id="element-8619">
                            Original
                          </div>
                          <div className="flex items-center gap-2" data-id="element-8620">
                            <span className="text-2xl" data-id="element-8621">
                              {originalProduct.image}
                            </span>
                            <span className="text-[14px] font-medium text-[#0F172A]" data-id="element-8622">
                              {originalProduct.name}
                            </span>
                          </div>
                        </div>
                        <ArrowRight className="w-6 h-6 text-[#94A3B8]" data-id="element-8623" />
                        <div className="flex-1 p-3 bg-[#F8FAFC] rounded-md" data-id="element-8624">
                          <div className="text-[12px] text-[#94A3B8] mb-1" data-id="element-8625">
                            Replacement
                          </div>
                          <div className="flex items-center gap-2" data-id="element-8626">
                            <span className="text-2xl" data-id="element-8627">
                              {replacementProduct.image}
                            </span>
                            <span className="text-[14px] font-medium text-[#0F172A]" data-id="element-8628">
                              {replacementProduct.name}
                            </span>
                          </div>
                        </div>
                      </div> : selectedProduct && <div className="p-4 bg-[#F8FAFC] rounded-md" data-id="element-8629">
                          <div className="text-[12px] text-[#94A3B8] mb-2" data-id="element-8630">
                            Product
                          </div>
                          <div className="flex items-center gap-2" data-id="element-8631">
                            <span className="text-2xl" data-id="element-8632">
                              {selectedProduct.image}
                            </span>
                            <span className="text-[14px] font-medium text-[#0F172A]" data-id="element-8633">
                              {selectedProduct.name}
                            </span>
                          </div>
                          <div className="mt-2 text-[14px] text-[#64748B]" data-id="element-8634">
                            Price: $
                            {(selectedProduct.subscriptionPrice || selectedProduct.price).toFixed(2)}{' '}
                            → ${selectedProduct.shopifyPrice.toFixed(2)}
                          </div>
                        </div>}

                    <div className="grid grid-cols-2 gap-4" data-id="element-8635">
                      <div className="p-3 bg-[#F8FAFC] rounded-md" data-id="element-8636">
                        <div className="text-[12px] text-[#94A3B8] mb-1" data-id="element-8637">
                          Subscriptions affected
                        </div>
                        <div className="text-[20px] font-semibold text-[#0F172A]" data-id="element-8638">
                          {selectedCount}
                        </div>
                      </div>
                      <div className="p-3 bg-[#F8FAFC] rounded-md" data-id="element-8639">
                        <div className="text-[12px] text-[#94A3B8] mb-1" data-id="element-8640">
                          Price change
                        </div>
                        <div className="text-[20px] font-semibold text-[#0F172A]" data-id="element-8641">
                          {operationType === 'sync' && selectedProduct ? `$${(selectedProduct.shopifyPrice - (selectedProduct.subscriptionPrice || selectedProduct.price)).toFixed(2)} increase` : hasPriceDifference ? `$${Math.abs(priceDifference).toFixed(2)} ${priceDifference > 0 ? 'increase' : 'decrease'}` : 'None'}
                        </div>
                      </div>
                    </div>

                    <div className="space-y-2 text-[14px]" data-id="element-8642">
                      <div className="flex justify-between" data-id="element-8643">
                        <span className="text-[#64748B]" data-id="element-8644">
                          Customer notification:
                        </span>
                        <span className="font-medium text-[#0F172A]" data-id="element-8645">
                          {notifyCustomers ? 'Yes' : 'No'}
                        </span>
                      </div>
                      <div className="flex justify-between" data-id="element-8646">
                        <span className="text-[#64748B]" data-id="element-8647">Timing:</span>
                        <span className="font-medium text-[#0F172A]" data-id="element-8648">
                          {timing === 'immediate' && 'Immediately'}
                          {timing === 'next-order' && 'On next order'}
                          {timing === 'scheduled' && `Scheduled for ${scheduledDate}`}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Warning */}
                  <div className="p-4 bg-[#FEE2E2] border border-[#FCA5A5] rounded-md flex items-start gap-3" data-id="element-8649">
                    <AlertTriangle className="w-5 h-5 text-[#B91C1C] flex-shrink-0 mt-0.5" data-id="element-8650" />
                    <div data-id="element-8651">
                      <p className="text-[14px] font-semibold text-[#B91C1C] mb-1" data-id="element-8652">
                        3 subscriptions have orders processing within 24 hours
                      </p>
                      <p className="text-[13px] text-[#B91C1C]" data-id="element-8653">
                        These will use the new{' '}
                        {operationType === 'replace' ? 'product' : 'price'}.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div className="p-6 border-t border-[#E2E8F0] flex items-center justify-end gap-3" data-id="element-8654">
                  <Button variant="outline" onClick={() => setShowConfirmModal(false)} data-id="element-8655">
                    Cancel
                  </Button>
                  <Button variant="primary" onClick={handleConfirmExecute} data-id="element-8656">
                    Confirm & Execute
                  </Button>
                </div>
              </div>
            </div>
          </>}

      {/* Processing Overlay */}
      {isProcessing && <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center" data-id="element-8657">
          <div className="bg-white rounded-lg p-8 flex flex-col items-center" data-id="element-8658">
            <RefreshCw className="w-12 h-12 text-[#0EA5E9] animate-spin mb-4" data-id="element-8659" />
            <p className="text-[18px] font-semibold text-[#0F172A]" data-id="element-8660">
              Processing{' '}
              {operationType === 'replace' ? 'replacement' : 'price sync'}...
            </p>
          </div>
        </div>}
    </div>;
}